import { compileAmbient } from '../janus-engine/compile/compile-ambient';
// audio-catalog.ts — the browser half of the audio system (owner 2026-09-10: the browser picks the sound).
//
// The server never chooses a take and never routes one. A roll frame carries only `roll_sound` — a REFERENCE
// ({v:2, rev, used?}, audio_helpers.php ft_roll_sound); the browser builds the route itself (roll-sound-route.ts) from
// the frame's ids, the items' per-phase pool keys in the encounter's `.catalog` (`assets.audio`,
// audio_presentation_inc.php) and this catalog's `rules`. This module maps pool keys to CDN files through the
// versioned catalog (audio_catalog_inc.php; boot island → window.FateTide.audioCatalog) and picks.
//
// Tier semantics mirror get_audio_info(): walk the tiers in order, the first with any take wins, a tier is a
// union of its pool keys (each take once), and a whole-category key ('damage') is the union of every
// 'damage/…' pool. A roll's pick is SEEDED by the row (roll_i) and made from the revision the frame names, so
// every client hears the same take. Voice lives in per-voice shards fetched on a voice's first line.

export const AUDIO_CATALOG_SCHEMA = 'ft.audio-catalog.v2';
export const AUDIO_VOICE_SCHEMA = 'ft.audio-voice.v1';
export const AUDIO_PHASES = ['cast', 'hit', 'heal', 'miss', 'whiff', 'applied', 'removed', 'death', 'attack'] as const;
export type AudioPhase = typeof AUDIO_PHASES[number];
export type AudioTiers = string[][];
export interface AudioPools { base: string; pools: Record<string, string[]>; mediaVersion?: string }
export interface AudioCatalog extends AudioPools {
    schema: typeof AUDIO_CATALOG_SCHEMA; rev: string;
    /** Ambience + music by id: '<amb|mx>/<file>' under `root` (what the soundboard and the ambience bed play by id). */
    root?: string; files?: Record<string, string>;
    /** The weapon/damage maps behind item refs (audio_presentation_inc.php ft_audio_taxonomy), for deriveAudioRefs. */
    taxonomy?: AudioTaxonomy | null;
    /** The static tables the roll route builder reads (audio_helpers.php ft_audio_roll_rules; roll-sound-route.ts). */
    rules?: unknown;
    /** Voice slug → shard rev, sealed into `rev` (build 12): a roll's voice lines resolve from the revision its frame names. */
    voices?: Record<string, string>;
}
export interface AudioTaxonomy {
    hit: Record<string, string>; swing: Record<string, string>; damage: Record<string, string[]>;
    spell_miss: Record<string, string>; piercing: string; daggers: string; dagger_piercing: string[];
}
export interface VoiceShard extends AudioPools { schema: typeof AUDIO_VOICE_SCHEMA; rev: string; voice: string }
export interface AudioTake { audioId: string; url: string; pool: string }
/** A frame's sound reference: the revision to pick from, item_id_used when it is not roll_item_id, `voice: 0` on a log message. */
export interface RollSound { v: 2; rev?: string; used?: string | 0; voice?: 0; sfx?: 0; incantation?: 0 }
/** A roll's route as roll-sound-route.ts builds it. */
export interface RollRouteTiers { sfx: AudioTiers; voice?: { caster?: AudioTiers; once?: string; target?: AudioTiers; stack?: true; queue?: true; casterAfter?: { tiers: AudioTiers; once: string }; ally?: { tiers: AudioTiers; once: string } } }
export type RollRouteBuilder = (catalog: AudioCatalog) => RollRouteTiers | null;

const ENTRY = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(\.[a-z0-9]{2,4})?$/i;
const REV = /^[0-9a-f]{16}$/;
const SLUG = /^[a-z0-9_]{1,80}$/;
const TERMINAL_TIER = ['die_roll'];
const FETCH_TIMEOUT_MS = 8000; // a stalled request is abandoned (and forgotten) rather than memoized forever

type Rec = Record<string, unknown>;
const rec = (v: unknown): Rec | null => (v && typeof v === 'object' && !Array.isArray(v) ? v as Rec : null);
const scalar = (v: unknown): unknown => (Array.isArray(v) ? v[0] : v);

function validPools(c: Rec): boolean {
    if (typeof c.rev !== 'string' || !REV.test(c.rev) || typeof c.base !== 'string' || !/^https:\/\/[^\s]+\/$/.test(c.base)) return false;
    const pools = rec(c.pools);
    if (!pools) return false;
    for (const entries of Object.values(pools)) {
        if (!Array.isArray(entries) || !entries.every(e => typeof e === 'string' && ENTRY.test(e))) return false;
    }
    return true;
}

const FILE = /^(amb|mx)\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\.[a-z0-9]{2,4}$/i;
/** Validated SFX catalog or null. */
export function audioCatalog(value: unknown): AudioCatalog | null {
    const c = rec(value);
    if (!c || c.schema !== AUDIO_CATALOG_SCHEMA || !validPools(c)) return null;
    if (c.files !== undefined) {
        const files = rec(c.files);
        if (!files || typeof c.root !== 'string' || !/^https:\/\/[^\s]+\/$/.test(c.root)
            || !Object.values(files).every(f => typeof f === 'string' && FILE.test(f))) return null;
    }
    if (c.voices !== undefined) {
        const voices = rec(c.voices);
        if (!voices || !Object.entries(voices).every(([slug, r]) => SLUG.test(slug) && typeof r === 'string' && REV.test(r))) return null;
    }
    return c as unknown as AudioCatalog;
}

/** Validated voice shard or null. */
export function voiceShard(value: unknown): VoiceShard | null {
    const c = rec(value);
    return c && c.schema === AUDIO_VOICE_SCHEMA && typeof c.voice === 'string' && validPools(c) ? c as unknown as VoiceShard : null;
}

const tierList = (v: unknown): AudioTiers | null =>
    Array.isArray(v) && v.every(t => Array.isArray(t) && t.length > 0 && t.every(k => typeof k === 'string' && k !== ''))
        ? (v as string[][]).map(t => [...t]) : null;

/** A frame's `roll_sound` reference, validated; null when absent or malformed (a v1 server route is not played). */
export function rollSound(value: unknown): RollSound | null {
    const s = rec(value);
    if (!s || s.v !== 2) return null;
    const out: RollSound = { v: 2 };
    if (typeof s.rev === 'string' && REV.test(s.rev)) out.rev = s.rev;
    if (typeof s.used === 'string' && REF_ID.test(s.used)) out.used = s.used.toUpperCase();
    else if (s.used === 0) out.used = 0; // the row used no item
    if (s.voice === 0) out.voice = 0;
    if (s.incantation === 0) out.incantation = 0;
    if (s.sfx === 0) out.sfx = 0;
    return out;
}

const unions = new WeakMap<AudioPools, Map<string, string[]>>();
/**
 * Entries for one key. Stored keys are item ids, '<cat>/<sub>', '<cat>#<name>', '~/<sub>' and voice keys; a bare
 * '<cat>' is never stored and expands to the union of its '<cat>/…' pools.
 */
export function poolEntries(source: AudioPools, key: string): string[] {
    const direct = source.pools[key];
    if (direct) return direct;
    if (!key || key.includes('/') || key.includes('#')) return [];
    let memo = unions.get(source);
    if (!memo) { memo = new Map(); unions.set(source, memo); }
    let union = memo.get(key);
    if (!union) {
        const seen = new Set<string>(), prefix = key + '/';
        for (const [k, entries] of Object.entries(source.pools)) if (k.startsWith(prefix)) for (const e of entries) seen.add(e);
        union = [...seen];
        memo.set(key, union);
    }
    return union;
}

/** The item's refs (`assets.audio`, v1) or null when it carries none / an older shape. */
export function audioRefs(item: unknown): Partial<Record<AudioPhase, AudioTiers>> | null {
    const raw = rec(rec(rec(item)?.assets)?.audio);
    if (!raw || raw.v !== 1) return null;
    const out: Partial<Record<AudioPhase, AudioTiers>> = {};
    for (const phase of AUDIO_PHASES) {
        const tiers = tierList(raw[phase]);
        if (tiers) out[phase] = tiers;
    }
    return out;
}

function itemId(item: unknown): string {
    const id = scalar(rec(rec(item)?.items)?.item_id);
    return typeof id === 'string' ? id.toUpperCase() : '';
}

const PRESENTATION_TYPES = new Set(['spell', 'curse', 'menta', 'sanguine', 'ability', 'quick_ability', 'full_ability', 'free_ability',
    'reaction_ability', 'attack_ability', 'passive_ability', 'opportunity_attack_ability']);
/** A layer's first scalar, upper-cased: the page's ftLayerValue when loaded, else the compiled buckets directly. */
export function layerOf(item: unknown, key: string): string {
    if (!rec(item)) return '';
    const reader = (compileAmbient() as { ftLayerValue?: (a: unknown, k: string) => unknown }).ftLayerValue;
    let v: unknown = typeof reader === 'function' ? reader(item, key) : undefined;
    if (v === undefined || v === null || v === '') v = rec(rec(item)?.item_layer_types)?.[key] ?? rec(rec(item)?.layers)?.[key];
    while (Array.isArray(v)) v = v[0];
    const r = rec(v);
    if (r) v = r.item_id ?? rec(r.items)?.item_id ?? r.value ?? r.id; // a bare ref, or a nested {items:{item_id}} shell / item
    return typeof v === 'string' || typeof v === 'number' ? String(v).toUpperCase() : '';
}
const upperKeys = <T>(m: Record<string, T> | undefined): Record<string, T> => {
    const out: Record<string, T> = {};
    for (const [k, v] of Object.entries(m ?? {})) out[k.toUpperCase()] = v;
    return out;
};

/**
 * The item's refs derived from its LAYERS with the catalog's taxonomy — for a cached definition that predates
 * `assets.audio`. Mirrors ft_audio_phase_refs() branch for branch (spell/ability, weapon, creature); a status's
 * leveled BASE is server-only, so a derived status carries its own + generic tiers.
 */
export function deriveAudioRefs(item: unknown, taxonomy: AudioTaxonomy | null | undefined): Partial<Record<AudioPhase, AudioTiers>> | null {
    if (!taxonomy || !rec(item)) return null;
    const type = String(scalar(rec(rec(item)?.items)?.item_type) ?? '');
    const id = itemId(item);
    const wc = layerOf(item, 'weapon_classification'), dt = layerOf(item, 'damage_type');
    const hitMap = upperKeys(taxonomy.hit), swingMap = upperKeys(taxonomy.swing), dmgMap = upperKeys(taxonomy.damage), missMap = upperKeys(taxonomy.spell_miss);
    const struck: AudioTiers = [];
    if (wc && hitMap[wc]) struck.push([`weapon/${hitMap[wc]}`]);
    const subs = dt && dt === String(taxonomy.piercing).toUpperCase() && wc && wc === String(taxonomy.daggers).toUpperCase() ? taxonomy.dagger_piercing : (dt ? dmgMap[dt] : undefined);
    if (subs?.length) struck.push(subs.map(s => `damage/${s}`));
    const refs: Partial<Record<AudioPhase, AudioTiers>> = {};
    if (id && PRESENTATION_TYPES.has(type)) {
        refs.cast = [['@']];
        refs.hit = [['@'], ...struck];
        refs.heal = [['@'], ['spell/heal']];
        refs.miss = dt && missMap[dt] ? [[`miss/${missMap[dt]}`], ['combat/miss_whiff']] : [['combat/miss_whiff']];
    } else if (struck.length) {
        refs.hit = struck;
    }
    if (wc) refs.whiff = swingMap[wc] ? [[`weapon/${swingMap[wc]}`], ['combat/miss_whiff']] : [['combat/miss_whiff']];
    if (type === 'status_effect' && id) {
        refs.applied = [['@/applied'], ['status_applied']];
        refs.removed = [['@/removed'], ['status_removed']];
    }
    if (type === 'creature' && id) {
        const ct = layerOf(item, 'creature_type');
        refs.death = [['@/death'], ...(REF_ID.test(ct) ? [[`${ct}/death`]] : []), ['blood_spill']];
        refs.attack = [['@/attack']];
    }
    return Object.keys(refs).length ? refs : null;
}

function expand(tiers: AudioTiers, id: string): AudioTiers {
    return tiers.map(t => t.map(k => (k === '@' ? id : k.startsWith('@/') ? id + k.slice(1) : k)));
}

/**
 * Concrete tiers for one sound of `item` (for client-born rows). For a `hit`, pass the attacking creature:
 * its own `attack` pool slots in right after the ability's own pool (or first, for a weapon).
 */
export function audioTiers(item: unknown, phase: AudioPhase, opts: { attacker?: unknown; taxonomy?: AudioTaxonomy | null } = {}): AudioTiers {
    const id = itemId(item);
    const refsOf = (it: unknown) => audioRefs(it) ?? deriveAudioRefs(it, opts.taxonomy);
    const tiers = expand(refsOf(item)?.[phase] ?? [], id);
    if (phase === 'hit' && opts.attacker) {
        const attackerTiers = expand(refsOf(opts.attacker)?.attack ?? [], itemId(opts.attacker));
        const at = tiers.length > 0 && tiers[0]!.length === 1 && tiers[0]![0] === id ? 1 : 0;
        tiers.splice(at, 0, ...attackerTiers);
    }
    tiers.push([...TERMINAL_TIER]);
    return tiers;
}

function take(source: AudioPools, entry: string, pool: string): AudioTake {
    const dot = entry.indexOf('.');
    return { audioId: dot < 0 ? entry : entry.slice(0, dot), url: audioMediaUrl(source.base + (dot < 0 ? `${entry}.mp3` : entry), source), pool };
}

function audioMediaUrl(url: string, source: AudioPools): string {
    if (!source.mediaVersion) return url;
    const parsed = new URL(url);
    parsed.searchParams.set('ct', source.mediaVersion);
    return parsed.href;
}

/** First tier with any take, uniform over that tier's union (each take once, like SQL IN). */
export function pickAudioTake(source: AudioPools, tiers: AudioTiers, random: () => number = Math.random): AudioTake | null {
    for (const tier of tiers) {
        const union = new Map<string, string>();
        for (const pool of tier) for (const entry of poolEntries(source, pool)) if (!union.has(entry)) union.set(entry, pool);
        if (union.size) {
            const entries = [...union];
            const [entry, pool] = entries[Math.min(entries.length - 1, Math.max(0, Math.floor(random() * entries.length)))]!;
            return take(source, entry, pool);
        }
    }
    return null;
}

/** Deterministic [0,1) stream from a string seed (FNV-1a → mulberry32): every client draws the same take. */
export function seededRandom(seed: string): () => number {
    let h = 0x811c9dc5;
    for (let i = 0; i < seed.length; i++) { h ^= seed.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
    let a = h || 1;
    return () => {
        a = (a + 0x6d2b79f5) >>> 0;
        let t = a;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

const byIdIndex = new WeakMap<AudioPools, Map<string, AudioTake>>();
/** Resolve an audioId named elsewhere (a relayed pick) to its URL. Null when this catalog does not hold it. */
export function audioTakeById(source: AudioPools, audioId: string): AudioTake | null {
    let index = byIdIndex.get(source);
    if (!index) {
        index = new Map();
        for (const [pool, entries] of Object.entries(source.pools)) {
            for (const entry of entries) {
                const t = take(source, entry, pool), key = t.audioId.toUpperCase();
                if (!index.has(key)) index.set(key, t);
            }
        }
        byIdIndex.set(source, index);
    }
    return index.get(String(audioId).toUpperCase()) ?? null;
}

// ── loading ────────────────────────────────────────────────────────────────────────────────────────────
interface BootCatalog { rev: string; api: string; voices: Record<string, string> }
function bootCatalog(): BootCatalog | null {
    const b = rec(rec((compileAmbient() as unknown as Rec).FateTide)?.audioCatalog);
    if (!b || typeof b.rev !== 'string') return null;
    const api = typeof b.voiceUrl === 'string' ? b.voiceUrl : (typeof b.url === 'string' ? b.url.split('?')[0]! : '');
    return api ? { rev: b.rev, api, voices: (rec(b.voices) ?? {}) as Record<string, string> } : null;
}

/** fetch → JSON with a hard timeout, so a stalled request fails (and is forgotten) instead of hanging forever. */
async function fetchJson(fetchImpl: typeof fetch, url: string): Promise<unknown> {
    const abort = typeof AbortController === 'function' ? new AbortController() : null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<never>((_, reject) => { timer = setTimeout(() => { abort?.abort(); reject(new Error('audio catalog timeout')); }, FETCH_TIMEOUT_MS); });
    try {
        const r = await Promise.race([fetchImpl(url, { credentials: 'same-origin', ...(abort ? { signal: abort.signal } : {}) }), timeout]);
        return r.ok ? await Promise.race([r.json(), timeout]) : null;
    } finally {
        if (timer !== undefined) clearTimeout(timer);
    }
}

function memoLoad<T>(cache: Map<string, Promise<T | null>>, key: string, load: () => Promise<T | null>): Promise<T | null> {
    const hit = cache.get(key);
    if (hit) return hit;
    const promise = load().catch(() => null).then(v => {
        if (!v && cache.get(key) === promise) cache.delete(key); // a failed load is forgotten so the next call retries
        return v;
    });
    cache.set(key, promise);
    return promise;
}

const catalogs = new Map<string, Promise<AudioCatalog | null>>();
const goneRevs = new Set<string>(); // revisions the server no longer holds: don't ask again, use the page's own
/**
 * An SFX catalog. With `rev` (the revision a roll's route was built against) only that exact revision is
 * accepted, so every client picks from the same pools; without it — or once that revision is gone — the
 * page's boot revision, where the endpoint may answer a newer one (only fresher).
 */
export async function loadAudioCatalog(fetchImpl: typeof fetch = fetch, rev?: string): Promise<AudioCatalog | null> {
    const boot = bootCatalog();
    if (!boot) return null;
    if (rev && rev !== boot.rev && !goneRevs.has(rev)) {
        const exact = await memoLoad(catalogs, rev, async () => {
            const c = audioCatalog(await fetchJson(fetchImpl, `${boot.api}?rev=${rev}`));
            if (c && c.rev !== rev) { goneRevs.add(rev); return null; }
            return c;
        });
        if (exact) return exact;
    }
    return memoLoad(catalogs, boot.rev, async () => {
        const c = audioCatalog(await fetchJson(fetchImpl, `${boot.api}?rev=${boot.rev}`));
        if (c) {
            loadedCatalog = c;
            (compileAmbient() as { __ftLoadedAudioCatalog?: AudioCatalog }).__ftLoadedAudioCatalog = c;
        }
        return c;
    });
}

let loadedCatalog: AudioCatalog | null = null;
/** The page's catalog if it has already loaded (it is warmed at idle): the synchronous read an action's instant pick needs. */
export function peekAudioCatalog(): AudioCatalog | null {
    // Standalone scene and websocket bundles have separate module instances on the same page.
    return (compileAmbient() as { __ftLoadedAudioCatalog?: AudioCatalog }).__ftLoadedAudioCatalog ?? loadedCatalog;
}

// ── client-truth action sounds (owner 2026-09-10: never tied to a saved roll) ──────────────────────────
export interface ActionSoundCue { slot: string; tiers: AudioTiers }
export interface ActionSoundPick { slot: string; audio_id: string; voice?: string; rev?: string }
export interface ActionSound { v: 1; rev?: string; picks: ActionSoundPick[] }
/** An action's voice cues (roll-sound-route.ts actionVoiceTiers): the caster speaks first, the target only when it has no take. */
export interface ActionVoice { caster?: AudioTiers; target?: AudioTiers }
/**
 * The ACTOR's pick for the action it just resolved: one take per cue, seeded by the operation, from the page's
 * loaded catalog — synchronous, so it is stamped on the command before the outbox freezes it and played the same
 * instant the result applies — plus ONE voice line from the voice shards the page has already loaded (warmed for
 * the encounter's voiced creatures). Null when nothing could be picked: the rows then keep their sound reference.
 */
export function pickActionSound(cues: ActionSoundCue[], seed: string, voice?: ActionVoice | null): ActionSound | null {
    const catalog = loadedCatalog;
    const picks: ActionSoundPick[] = [];
    if (catalog) {
        for (const cue of cues.slice(0, 7)) {
            const t = pickAudioTake(catalog, cue.tiers, seededRandom(`${seed}|${cue.slot}`));
            if (t) picks.push({ slot: cue.slot, audio_id: t.audioId });
        }
    }
    const spoken = pickActionVoice(voice, seed);
    if (spoken) picks.push(spoken);
    return picks.length ? { v: 1, ...(catalog ? { rev: catalog.rev } : {}), picks } : null;
}

/**
 * One voice line for an action, from shards already in memory (never waits on a download): caster first, else target.
 * If a voice the decision depends on is not in memory the pick is NULL — "the caster has no line" must never be
 * guessed from a missing file, or the target would speak over the caster's real line.
 */
export function pickActionVoice(voice: ActionVoice | null | undefined, seed: string): ActionSoundPick | null {
    // undefined = nothing to say from these tiers; null = cannot tell (a shard is not loaded).
    const from = (tiers: AudioTiers | undefined, salt: string): ActionSoundPick | null | undefined => {
        if (!tiers?.length) return undefined;
        const shards = [...new Set(tiers.flat().map(voiceSlugOf).filter(Boolean))].map(s => loadedShards.get(s));
        if (!shards.length || shards.some(s => !s)) return null;
        const held = shards as VoiceShard[];
        const t = pickAudioTake({ base: held[0]!.base, pools: Object.assign({}, ...held.map(s => s.pools)) }, tiers, seededRandom(`${seed}|${salt}`));
        const slug = t ? voiceSlugOf(t.pool) : '';
        const rev = slug ? loadedShards.get(slug)?.rev : undefined;
        return t && slug ? { slot: 'voice', audio_id: t.audioId, voice: slug, ...(rev ? { rev } : {}) } : undefined;
    };
    const caster = from(voice?.caster, 'voice');
    if (caster !== undefined) return caster;
    return from(voice?.target, 'target') ?? null;
}

/** `slot` is the actor's cue slot (attack / reaction / hit / whiff / rider…) — impact slots time to the swing. */
export interface ActionTake { url: string; voice: boolean; slot?: string }
/** A relayed action sound → its takes: effects from the revision they were picked in (else the page's catalog), the voice line from its shard. */
export async function actionSoundTakes(value: unknown, fetchImpl: typeof fetch = fetch, part: 'all' | 'effects' | 'voice' = 'all'): Promise<ActionTake[]> {
    const s = rec(value);
    if (!s || s.v !== 1 || !Array.isArray(s.picks)) return [];
    const picks = s.picks.map(p => rec(p)).filter((p): p is Rec => !!p && REF_ID.test(String(p.audio_id ?? ''))).slice(0, 8);
    const effects = part === 'voice' ? [] : picks.filter(p => p.slot !== 'voice');
    const spoken = part === 'effects' ? undefined : picks.find(p => p.slot === 'voice' && typeof p.voice === 'string' && SLUG.test(p.voice));
    const out: ActionTake[] = [];
    if (effects.length) {
        const rev = typeof s.rev === 'string' && REV.test(s.rev) ? s.rev : undefined;
        const catalog = rev && loadedCatalog?.rev === rev ? loadedCatalog : await loadAudioCatalog(fetchImpl, rev);
        if (catalog) for (const p of effects) { const url = audioTakeById(catalog, String(p.audio_id))?.url; if (url) out.push({ url, voice: false, ...(typeof p.slot === 'string' ? { slot: p.slot } : {}) }); }
    }
    if (spoken) {
        // The shard revision the actor picked from, so every peer finds the same take (else the page's own).
        const slug = String(spoken.voice), rev = typeof spoken.rev === 'string' && REV.test(spoken.rev) ? spoken.rev : undefined;
        const held = loadedShards.get(slug);
        const shard = held && (!rev || held.rev === rev) ? held : ((await loadVoiceShard(slug, fetchImpl, rev)) ?? held ?? null);
        const url = shard ? audioTakeById(shard, String(spoken.audio_id))?.url : undefined;
        if (url) out.push({ url, voice: true });
    }
    return out;
}

/** A relayed action sound → its files (effects and voice alike). */
export async function actionSoundFiles(value: unknown, fetchImpl: typeof fetch = fetch): Promise<string[]> {
    return (await actionSoundTakes(value, fetchImpl)).map(t => t.url);
}

const shards = new Map<string, Promise<VoiceShard | null>>();
const loadedShards = new Map<string, VoiceShard>(); // slug → the shard in memory: an action's instant voice pick reads it synchronously
/** One voice's shard (approved + unreviewed lines), fetched on its first line — the exact revision when named. */
export function loadVoiceShard(slug: string, fetchImpl: typeof fetch = fetch, rev?: string): Promise<VoiceShard | null> {
    const boot = bootCatalog();
    if (!boot || !SLUG.test(slug)) return Promise.resolve(null);
    const want = rev ?? boot.voices[slug] ?? '';
    // A named revision that IS the page head's reuses the (usually already warmed) head load when that load returned
    // exactly it — the common case, so a roll never re-downloads a shard the page holds.
    if (rev && rev === boot.voices[slug]) {
        // An exact load already made (the head once failed or answered newer) wins: never re-wait on a head retry.
        const exact = shards.get(`${slug}|=${rev}`);
        if (exact) return exact;
        return loadVoiceShard(slug, fetchImpl).then(head => (head && head.rev === rev ? head : loadExactVoiceShard(boot, slug, fetchImpl, rev)));
    }
    return rev ? loadExactVoiceShard(boot, slug, fetchImpl, rev) : loadHeadVoiceShard(boot, slug, fetchImpl, want);
}
/** NAMED (exact) and HEAD loads never share a memo entry: a head load may be answered with a newer body, which must not
 *  satisfy a later exact request for the revision it asked for. */
function loadExactVoiceShard(boot: BootCatalog, slug: string, fetchImpl: typeof fetch, rev: string): Promise<VoiceShard | null> {
    return fetchVoiceShard(`${slug}|=${rev}`, boot, slug, fetchImpl, rev, rev);
}
function loadHeadVoiceShard(boot: BootCatalog, slug: string, fetchImpl: typeof fetch, want: string): Promise<VoiceShard | null> {
    return fetchVoiceShard(`${slug}|head:${want}`, boot, slug, fetchImpl, want, undefined);
}
function fetchVoiceShard(key: string, boot: BootCatalog, slug: string, fetchImpl: typeof fetch, want: string, rev: string | undefined): Promise<VoiceShard | null> {
    return memoLoad(shards, key, async () => {
        const shard = voiceShard(await fetchJson(fetchImpl, `${boot.api}?voice=${encodeURIComponent(slug)}${want ? `&rev=${want}` : ''}`));
        if (!shard || shard.voice !== slug) return null;
        // A NAMED revision is exact (the frame's or the relaying actor's): a different body would pick a different take.
        if (rev && shard.rev !== rev) return null;
        // The instant action pick reads the page's own HEAD shard only; a frame's pinned revision never becomes it.
        if (!rev) loadedShards.set(slug, shard);
        return shard;
    });
}

/** Load the shards of these voices ahead of time (the encounter's voiced creatures), so an action's voice pick is instant. */
const warmTried = new Map<string, number>(); // slug → last attempt: a missing / failing voice is retried at most once a minute
export function warmVoiceShards(slugs: Iterable<string>, fetchImpl: typeof fetch = fetch): void {
    const now = Date.now();
    for (const slug of new Set(slugs)) {
        if (loadedShards.has(slug) || now - (warmTried.get(slug) ?? -Infinity) < 60_000) continue;
        warmTried.set(slug, now);
        void loadVoiceShard(slug, fetchImpl);
    }
}

export interface AudioFile { type: 'sfx' | 'amb' | 'mx'; url: string; audioId: string }
const REF_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const REF_URL = /^(sfx|amb|mx):(https:\/\/[^\s/]+\/audio\/(?:sfx|amb|mx)\/[0-9a-f-]{36}\.[a-z0-9]{2,4}(?:\?ct=[0-9]{8})?)$/i;
/**
 * A play-by-id reference → its file, resolved in the browser (the retired play_audio_file_inc.php did this on
 * the server, R-016). `ref` is an audio id (ambience / music from `files`, effects from the pools) or, for the
 * admin audio list whose rows can be unreviewed, an explicit '<type>:<CDN url>'.
 */
export async function resolveAudioFile(ref: string, fetchImpl: typeof fetch = fetch): Promise<AudioFile | null> {
    const direct = REF_URL.exec(String(ref).trim());
    if (direct) {
        // Only the catalog's own CDN origin, and never a userinfo / port trick ('cdn.fatetide.com@elsewhere').
        let u: URL;
        try { u = new URL(direct[2]!); } catch { return null; }
        if (u.username || u.password || u.port) return null;
        const catalog = await loadAudioCatalog(fetchImpl);
        const allowed = catalog ? new URL(catalog.root ?? catalog.base).origin : null;
        if (!allowed || u.origin !== allowed) return null;
        const url = audioMediaUrl(u.href, catalog!);
        return { type: direct[1]!.toLowerCase() as AudioFile['type'], url, audioId: url.slice(url.lastIndexOf('/') + 1).split('.')[0]! };
    }
    const id = String(ref).trim();
    if (!REF_ID.test(id)) return null;
    const catalog = await loadAudioCatalog(fetchImpl);
    if (!catalog) return null;
    const file = catalog.files?.[id.toUpperCase()];
    if (file && catalog.root) return { type: file.slice(0, file.indexOf('/')) as AudioFile['type'], url: audioMediaUrl(catalog.root + file, catalog), audioId: id };
    const sfx = audioTakeById(catalog, id);
    return sfx ? { type: 'sfx', url: sfx.url, audioId: sfx.audioId } : null;
}

/** 'voice/<slug>__line' | 'voice~/<slug>__line' → slug; '' for anything else. */
export function voiceSlugOf(key: string): string {
    const m = /^voice~?\/([a-z0-9_]+?)__/.exec(key);
    return m ? m[1]! : '';
}

/** A voice the decision depends on could not be loaded (failed / timed out) — distinct from "loaded, has no take". */
const VOICE_UNAVAILABLE = Symbol('voice-unavailable');
type VoicePools = AudioPools | null | typeof VOICE_UNAVAILABLE;
/**
 * The pools of every voice these tiers name, each shard at the revision `revs` pins (the catalog the route was built
 * against), else the page's boot head. Null = the tiers name no voice; VOICE_UNAVAILABLE = a named shard did not load,
 * so "this speaker has no line" cannot be concluded (the target must not speak over a caster line another client plays).
 */
function voiceSources(tiers: AudioTiers | undefined, fetchImpl: typeof fetch, revs?: Record<string, string>): Promise<VoicePools> {
    if (!tiers?.length) return Promise.resolve(null);
    const slugs = [...new Set(tiers.flat().map(voiceSlugOf).filter(Boolean))];
    if (!slugs.length) return Promise.resolve(null);
    // A manifest that does not list a voice means that voice had NO lines at this revision: it contributes empty pools,
    // never the page head's (which may hold lines added later). Only a catalog without any manifest reads the head.
    const pinned = slugs.filter(s => !revs || typeof revs[s] === 'string');
    return Promise.all(pinned.map(s => loadVoiceShard(s, fetchImpl, revs?.[s]))).then(list => {
        if (list.some(s => !s)) return VOICE_UNAVAILABLE;
        const loaded = list as VoiceShard[];
        return { base: loaded[0]?.base ?? '', pools: Object.assign({}, ...loaded.map(s => s.pools)) };
    }, () => VOICE_UNAVAILABLE);
}

/** `p`, or `fallback` once `deadline` (epoch ms) passes first — a stalled download never holds the roll queue past its frame's deadline. */
function withinDeadline<T>(p: Promise<T>, deadline: number, fallback: T): Promise<T> {
    if (!Number.isFinite(deadline)) return p;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const late = new Promise<T>(resolve => { timer = setTimeout(() => resolve(fallback), Math.max(0, deadline - Date.now())); });
    return Promise.race([p, late]).finally(() => { if (timer !== undefined) clearTimeout(timer); });
}

const spokenOnce = new Map<string, number>();
let resolveQueue: Promise<unknown> = Promise.resolve();
/**
 * Everything a roll frame should play: the effect take and the voice take, each seeded by the row.
 *
 * `build` turns the frame into its route (roll-sound-route.ts rollSoundRoute over the page's context). It runs the
 * moment the frame ARRIVES whenever a catalog is already loaded — so it reads the encounter as the event left it (a
 * damage row lands before its damage reaches the target's row) — and only on a cold page once the catalog arrives.
 *
 * Frames resolve strictly in ARRIVAL order (one queue per page), so the first row of a cast — the same row on
 * every screen — is the one that claims its incantation. Voice precedence is the director's: the caster speaks
 * unless that actor already spoke under `once` — then nobody does — and the target speaks only when the caster has
 * no take. A frame resolved after `deadline` plays nothing and claims nothing, so a cold-load straggler cannot
 * silence the cast's real line.
 */
export function resolveRollSound(roll: Rec, fetchImpl: typeof fetch = fetch, deadline = Infinity, build?: RollRouteBuilder): Promise<{ sfx: AudioTake | null; voice: AudioTake | null; voiceAfter?: AudioTake | null; queue?: true }> {
    const sound = rollSound(roll.roll_sound);
    const none = { sfx: null, voice: null };
    if (!sound || !build) return Promise.resolve(none);
    const safeBuild = (c: AudioCatalog): RollRouteTiers | null => { try { return build(c); } catch { return null; } };
    // Early build only from the frame's OWN revision (rules / taxonomy / voice pins differ between revisions; the arrival
    // snapshot is the caller's capture, so a later build still reads the encounter as the event left it). An early null
    // (no rules, or nothing to play) is retried once against the resolved catalog.
    const early = loadedCatalog && (!sound.rev || loadedCatalog.rev === sound.rev) ? loadedCatalog : null;
    let route: RollRouteTiers | null | undefined = early ? (safeBuild(early) ?? undefined) : undefined;
    // Start every download now, concurrently; only the pick/claim step waits its turn in the queue.
    const catalogP = loadAudioCatalog(fetchImpl, sound.rev);
    let casterP = route !== undefined ? voiceSources(route?.voice?.caster, fetchImpl, early?.voices) : null;
    let targetP = route !== undefined ? voiceSources(route?.voice?.target, fetchImpl, early?.voices) : null;
    let afterP = route !== undefined ? voiceSources(route?.voice?.casterAfter?.tiers, fetchImpl, early?.voices) : null;
    let allyP = route !== undefined ? voiceSources(route?.voice?.ally?.tiers, fetchImpl, early?.voices) : null;
    const run = async () => {
        // Every wait is bounded by this frame's deadline, so one stalled download cannot hold the frames behind it.
        const catalog = await withinDeadline(catalogP, deadline, null);
        if (route === undefined) {
            route = catalog ? safeBuild(catalog) : null;
            casterP = voiceSources(route?.voice?.caster, fetchImpl, catalog?.voices);
            targetP = voiceSources(route?.voice?.target, fetchImpl, catalog?.voices);
            afterP = voiceSources(route?.voice?.casterAfter?.tiers, fetchImpl, catalog?.voices);
            allyP = voiceSources(route?.voice?.ally?.tiers, fetchImpl, catalog?.voices);
        }
        if (Date.now() > deadline || !route) return none;
        const seed = String(roll.roll_i ?? roll.roll_id ?? '');
        // The route is complete as built: it ends in 'die_roll' wherever get_audio_info() falls back to the clatter,
        // and deliberately does not where the director ruled silence (party-vote verdicts).
        const sfx = catalog && route.sfx.length ? pickAudioTake(catalog, route.sfx, seededRandom(`${seed}|sfx`)) : null;
        let voice: AudioTake | null = null;
        const spoken = route.voice;
        // Each voice is bounded by the deadline ON ITS OWN (a voice still downloading then counts as unavailable: the
        // effect still plays, the voice does not). The target is awaited only when the caster cannot speak or the row stacks.
        const held = (p: VoicePools): AudioPools | null => (p && p !== VOICE_UNAVAILABLE ? p : null);
        const casterPools = await withinDeadline(casterP ?? Promise.resolve(null), deadline, VOICE_UNAVAILABLE as VoicePools);
        // The caster's shard did not load: whether it has a line is unknown, so nobody speaks (never the target over it).
        if (spoken?.caster && casterPools === VOICE_UNAVAILABLE) return { sfx, voice: null };
        const caster = held(casterPools) && spoken?.caster ? pickAudioTake(held(casterPools)!, spoken.caster, seededRandom(`${seed}|voice`)) : null;
        const targetPools = !caster || spoken?.stack
            ? await withinDeadline(targetP ?? Promise.resolve(null), deadline, VOICE_UNAVAILABLE as VoicePools) : null;
        // Past the deadline nothing is spoken or CLAIMED (a late row must not use up the cast's incantation).
        if (Date.now() > deadline) return { sfx, voice: null };
        const targetHeld = held(targetPools);
        const claim = (once: string): boolean => {
            if (!once) return true;
            if (spokenOnce.has(once)) return false;
            spokenOnce.set(once, Date.now());
            if (spokenOnce.size > 500) for (const k of [...spokenOnce.keys()].slice(0, 100)) spokenOnce.delete(k);
            return true;
        };
        if (caster) {
            const once = spoken?.once ?? '';
            if (claim(once)) voice = caster;
        } else if (targetHeld && spoken?.target) {
            voice = pickAudioTake(targetHeld, spoken.target, seededRandom(`${seed}|target`));
        }
        // A STACKED row (a stealth spot, owner 2026-09-16) plays both: the spotter's line, then the caught creature's.
        let voiceAfter: AudioTake | null = null;
        let queuedExtra = false;
        if (caster && spoken?.stack && targetHeld && spoken.target) {
            const target = pickAudioTake(targetHeld, spoken.target, seededRandom(`${seed}|target`));
            if (voice) voiceAfter = target; else voice = target;
        }
        // The extra caster bark (kill) keeps its own slot; it follows the row's existing voice, or queues alone.
        // A stacked target already occupies voiceAfter, so never displace it with a third line.
        for (const [extra, pending, salt] of [[spoken?.casterAfter, afterP, 'kill'], [spoken?.ally, allyP, 'ally_down']] as const) {
            if (!extra || voiceAfter) continue;
            const pools = held(await withinDeadline(pending ?? Promise.resolve(null), deadline, VOICE_UNAVAILABLE as VoicePools));
            if (Date.now() > deadline || !pools) continue;
            const take = pickAudioTake(pools, extra.tiers, seededRandom(`${seed}|${salt}`));
            if (!take || !claim(extra.once)) continue;
            if (voice) voiceAfter = take;
            else { voice = take; queuedExtra = extra === spoken?.casterAfter; }
        }
        if (voiceAfter) return { sfx, voice, voiceAfter };
        // A QUEUED line (a concentration end) waits for whatever the channel is already saying.
        return voice && spoken?.queue && (queuedExtra || (caster && !spoken.casterAfter)) ? { sfx, voice, queue: true as const } : { sfx, voice };
    };
    const result = resolveQueue.then(run, run);
    resolveQueue = result.catch(() => undefined);
    return result;
}
